import { Row, RowItemType, Section, SectionType, SelectorIdentifier, UIElementType } from '../../models';
import { calculateMaintenanceInterval, convertDate, createSmartTranslationMessage } from '../../models/utils';
import { MonitorSeriesNames } from '../../../location-card.constants';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { LocationCardMonitorSeries } from 'app/shared/models/monitorsetting';
import { channelInstallationConfig } from './installation-types/channel';
import { InstallationSeries, InstallationType } from 'app/shared/models/location-details';
import {
    attachSectionToRow,
    elevationAlarmConversion,
    enableField,
    loadInstallationTypes,
    setGlobalConfig,
    pickMapCoords,
    removeSection,
    setFieldValueAction,
    statusChange,
    tagKeyChange,
} from '../../actions';
import { BasicInfoCommonFieldIds } from '../common.models';
import { ForeSiteULalarmsFieldIds, ForesiteULSectionIds } from './base-config-models';
import { BASE_MONITOR_SERIES } from '../../constants/base';
import { InstallationTypeSectionIds } from './installation-types/config-models';
import { elevationInstallationConfig } from './installation-types/elevation';
import { ValidatorIdentifier } from '../../models/validator-identifier';
import { RainGaugeLocation, LocationCardStatus, SampleRateElement } from '../../../models/location-card.model';
import { field, FieldActions } from '../../core/field-schema';
import { required, maxLength, pattern, custom, asyncCustom } from '../../core/validator-builders';
import { ForesiteULLocationCardState, ForesiteULMonitorConfiguration } from './models';
import { TextField } from '../../models/field-models';
import { LocationStatus, RainLocation } from 'app/shared/models/locations';
import { Action } from '@ngrx/store';

export const customPropertyRowGenerator = (index: number, populateValues?: { key: string; value: string }): Row => {
    return {
        info: {
            id: `custom-property-${index}`,
            panelClass: 'custom-50p',
        },
        items: [
            field.autocomplete<string, string>(`${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`, 'key', {
                optionsSelector: SelectorIdentifier.TagKeyAutocomplete,
                value: populateValues?.key,
                placeholder: 'ADD_EDIT.INPUT_TAG_NAME',
                validators: [required(), maxLength({ value: 128 })],
                panelClass: 'width-90',
                clearable: false,
                autoClearOnFocus: false,
                displayMapper: (option) => option,
                valueMapper: (option) => option,
                events: {
                    onBlur: (value) => [
                        tagKeyChange({
                            fieldId: `${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`,
                            newValue: value,
                            isMp1: true,
                        }),
                    ],
                },
            }),
            field.text(`${BasicInfoCommonFieldIds.CustomPropertyValue}-${index}`, 'value', {
                value: populateValues?.value,
                placeholder: 'ADD_EDIT.INPUT_TAG_VALUE',
                validators: [required(), maxLength({ value: 128 })],
                panelClass: 'width-90',
            }),
        ],
    };
};

/**
 * ForeSITE UL form configuration
 */
export const basicInfo = (state: ForesiteULLocationCardState): Section => {
    const payload = state.locationDetails && state.locationDetails.length > 0 ? state.locationDetails[0] : null;
    const settings = state.settings;
    const monitorConfig = state.monitorConfiguration as ForesiteULMonitorConfiguration;

    const rainGuage =
        payload && payload.assignedRainGaugeLocationId
            ? state.options.rainGauges.data.find((v) => v.locationId === payload.assignedRainGaugeLocationId)
            : null;

    const isEditMode = !!payload;
    const isSwitchMonitor = payload && payload.series.toLowerCase() !== MonitorSeriesNames.ForeSITE_UL.toLowerCase();
    const showCoordsButton = state.locationCardData.enableSelectOnMap;

    const basicInfoConfig: Section = {
        info: {
            id: ForesiteULSectionIds.BasicInfo,
            title: 'ADD_EDIT.ADD_EDIT_INFORMATION',
            displayTitle: true,
            expanded: true,
            expandable: true,
            panelClass: ForesiteULSectionIds.BasicInfo,
        },
        rows: [
            // Row 1: Location Name and Series
            {
                info: {
                    id: 'basic-info-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.LocationName, 'locationName', {
                        value: isEditMode ? payload.locationName : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_NAME',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            custom({
                                identifier: ValidatorIdentifier.UniqueLocationName,
                                message: 'COMMON.LOCATION_NAME_EXISTS',
                            }),
                            maxLength({
                                value: 63,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.trim().charAt(0).toUpperCase() + value.trim().slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    field.select<LocationCardMonitorSeries, string>(BasicInfoCommonFieldIds.Series, 'series', {
                        panelClass: 'custom-width',
                        value: BASE_MONITOR_SERIES.find(
                            (v) => v.value.toLowerCase() === MonitorSeriesNames.ForeSITE_UL.toLowerCase(),
                        )?.value,
                        placeholder: 'COMMON.MONITOR_SERIES_PLACEHOLDER',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.MonitorSeriesOptions,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: (value) => [
                                enableField({ fieldId: BasicInfoCommonFieldIds.InstallationType }),
                                loadInstallationTypes({ installationId: value.monitorSeriesID }),
                                setGlobalConfig({ monitorSeriesName: value.value }),
                            ],
                        },
                    }),
                ],
            },

            // Row 2: Serial Number and Status
            {
                info: {
                    id: 'basic-info-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.SerialNumber, 'serialnumber', {
                        panelClass: 'custom-width',
                        value: payload ? payload.serialNumber : null,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SERIAL_STRING',
                        validators: [
                            required(),
                            maxLength({ value: 10 }),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericPattern,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.SERIAL_NUMBER_ERROR',
                            }),
                            asyncCustom({
                                identifier: ValidatorIdentifier.UniqueSerialNumber,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.SERIAL_NUMBER_EXISTS_ERROR',
                            }),
                        ],
                        events: {
                            onInput: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.replace(/[^a-zA-Z0-9-]/g, '');
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    {
                        id: BasicInfoCommonFieldIds.StatusSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'status-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'status-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'status-row',
                                        panelClass: 'status-inner-row flex align-items-center gap-4',
                                    },
                                    items: [
                                        field.select<LocationCardStatus, number>(
                                            BasicInfoCommonFieldIds.Status,
                                            'status',
                                            {
                                                value: isEditMode ? payload.status : 0,
                                                panelClass: 'custom-width',
                                                disabled: !isEditMode,
                                                label: 'Status',
                                                valueMapper: (option) => option.key,
                                                displayMapper: (option) => option.name,
                                                validators: [required()],
                                                optionsSelector: SelectorIdentifier.LocationStatuses,
                                                events: {
                                                    onChange: () => [statusChange({ isMp1: true })],
                                                },
                                            },
                                        ),
                                        field.select<number, number>(
                                            BasicInfoCommonFieldIds.MaintenanceInterval,
                                            'maintenanceInterval',
                                            {
                                                value: isEditMode
                                                    ? calculateMaintenanceInterval(payload.maintenanceEnd)
                                                    : 2,
                                                label: 'CUSTOMER_EDITOR.HOURS',
                                                valueMapper: (option) => option,
                                                displayMapper: (option) => String(option),
                                                panelClass: 'width-15',
                                                validators: [required()],
                                                visible: isEditMode
                                                    ? payload.status === LocationStatus.Maintenance
                                                    : false,
                                                optionsSelector: SelectorIdentifier.MaintenanceIntervals,
                                            },
                                        ),
                                        {
                                            id: BasicInfoCommonFieldIds.MaintenanceEnd,
                                            name: 'maintenanceEnd',
                                            panelClass: 'width-25 field-status-info',
                                            label: isEditMode
                                                ? createSmartTranslationMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.RETURN_TO_ACTIVE', {
                                                    date: convertDate(payload.maintenanceEnd, state.customer?.dateFormat, state.customer?.timeFormat)
                                                  })
                                                : 'CUSTOMER_EDITOR.HOURS',
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Text,
                                            visible: isEditMode ? payload.status === LocationStatus.Maintenance : false,
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },

            // Row 3: Description only (no APN Provider)
            {
                info: {
                    id: 'basic-info-row-3',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.Description, 'description', {
                        value: isEditMode ? payload.description : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LOCATION',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            maxLength({
                                value: 257,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                ],
            },

            // Row 4: Assigned Rain Gauge (no Manhole Depth for UL)
            {
                info: {
                    id: 'basic-info-row-4',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.autocomplete<RainLocation, string>(
                        BasicInfoCommonFieldIds.AssignedRainGauge,
                        'assignedRainGauge',
                        {
                            value: rainGuage ? rainGuage.locationName : null,
                            autoClearOnFocus: true,
                            panelClass: 'custom-width',
                            label: 'Assigned Rain Gauge',
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ASSIGNED',
                            clearable: true,
                            optionsSelector: SelectorIdentifier.RainGaugeAutocomplete,
                            displayMapper: (option) => option.locationName,
                            valueMapper: (option) => option.locationName,
                            validators: [
                                maxLength({
                                    value: 50,
                                }),
                            ],
                        },
                    ),
                ],
            },

            // Row 5: Address (formerly Manhole Address) and Coordinates
            {
                info: {
                    id: 'address-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.ManholeAddress, 'manholeaddress', {
                        value: isEditMode ? payload.manholeAddress : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MANHOLE_ADDRESS',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            maxLength({
                                value: 64,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    {
                        id: BasicInfoCommonFieldIds.CoordinatesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CoordinatesSection,
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'coordinates-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'coordinates-row',
                                        panelClass: `coordinates-inner-row ${
                                            showCoordsButton ? 'flex-end' : 'justify-space-between'
                                        }`,
                                    },
                                    items: [
                                        field.number(BasicInfoCommonFieldIds.Latitude, 'latitude', {
                                            value: isEditMode ? payload.latitude : 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LATITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.latitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        field.number(BasicInfoCommonFieldIds.Longitude, 'longitude', {
                                            value: isEditMode ? payload.longitude : 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LONGITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.longitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        {
                                            id: 'move-location-icon',
                                            name: 'moveLocationIcon',
                                            type: RowItemType.UI,
                                            visible: !!showCoordsButton,
                                            uiType: UIElementType.Image,
                                            imgSrc: '/assets/images/icons/move_icon.png',
                                            imgHeight: 18,
                                            imgWidth: 18,
                                            panelClass: 'move-icon',
                                            events: {
                                                onClick: () => [pickMapCoords()],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },

            // Row 6: Installation Date (full row)
            {
                info: {
                    id: 'installation-date-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.date(BasicInfoCommonFieldIds.InstallationDate, 'installationDate', {
                        label: 'Installation Date',
                        placeholder: 'Select date',
                        dateFormat: settings.dateFormat,
                        datePickerType: 'date',
                        timeInterval: 1,
                        panelClass: 'custom-width',
                        value: isEditMode ? payload.installationDate : new Date(),
                    }),
                ],
            },

            // Row 7: Installation Type and Sample Rate
            {
                info: {
                    id: 'installation-type-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<InstallationType, string>(
                        BasicInfoCommonFieldIds.InstallationType,
                        'installationType',
                        {
                            preserveOldValues: false,
                            value:
                                isEditMode && !isSwitchMonitor
                                    ? `${payload.installationType}: ${payload.installationShapeTypeID}`
                                    : '',
                            placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.INSTALLATION_TYPE',
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.InstallationTypes,
                            panelClass: 'custom-width',
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            events: {
                                onChange: (value: InstallationType) => {
                                    const toDispatch: Action[] = [
                                        elevationAlarmConversion({
                                            newInstallationSeries: value.series,
                                            installationTypeFieldId: BasicInfoCommonFieldIds.InstallationType,
                                            alarmFieldIds: {
                                                lowLevelThreshold: ForeSiteULalarmsFieldIds.LowLevelThreshold,
                                                highLevelThreshold: ForeSiteULalarmsFieldIds.HighLevelThreshold,
                                                highHighThreshold: ForeSiteULalarmsFieldIds.HighHighThreshold,
                                            },
                                            isMetric: settings.isMetric,
                                            elevationUnit: settings.elevationUnit,
                                            depthUnit: settings.depthUnit,
                                        }),
                                        setFieldValueAction({ fieldId: BasicInfoCommonFieldIds.InstallationType, value: value.value }),
                                    ];
                                    const installationSections = [
                                        InstallationTypeSectionIds.Elevation,
                                        InstallationTypeSectionIds.Channel,
                                    ];

                                    installationSections.forEach((sectionId) => {
                                        toDispatch.push(
                                            removeSection({
                                                sectionId,
                                                parentSectionId: ForesiteULSectionIds.BasicInfo,
                                            }),
                                        );
                                    });
                                    if (value.series === InstallationSeries.Channel) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: ForesiteULSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Channel,
                                                sectionConfig: channelInstallationConfig(state),
                                            }),
                                        );
                                    } else if (value.series === InstallationSeries.Elevation) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: ForesiteULSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Elevation,
                                                sectionConfig: elevationInstallationConfig(state),
                                            }),
                                        );
                                    }

                                    return toDispatch;
                                },
                            },
                        },
                    ),
                    field.select<SampleRateElement, number>(BasicInfoCommonFieldIds.SampleRate, 'samplerate', {
                        value: isEditMode ? monitorConfig.samplerate : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SAMPLE_RATE',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.ForeSiteSampleRates,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                    }),
                ],
            },

            // Custom properties section
            {
                info: {
                    id: 'custom-properties-container-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                                title: 'ADD_EDIT.TAGS_SECTION_NAME',
                                displayTitle: false,
                                displayActionBtns: true,
                                type: SectionType.Array,
                                expanded: false,
                                expandable: false,
                                itemGenerator: customPropertyRowGenerator,
                                panelClass: 'custom-properties-panel',
                                maxItems: 64,
                            },
                            rows:
                                isEditMode && payload.customProperties
                                    ? payload.customProperties.map((item, index) =>
                                          customPropertyRowGenerator(index, item),
                                      )
                                    : [],
                        },
                    },
                ],
            },
        ],
    };

    if (!isEditMode || isSwitchMonitor) {
        return basicInfoConfig;
    }

    const installationConfigGenerator = pickInstallationTypeConfig(payload.installationShapeTypeID);

    if (!installationConfigGenerator) {
        return basicInfoConfig;
    }

    const installationConfig = installationConfigGenerator(state);
    const sectionId = getInstallationTypeSectionId(payload.installationShapeTypeID);

    const installationTypeRowIndex = basicInfoConfig.rows.findIndex((row) => row.info.id === 'installation-type-row');
    basicInfoConfig.rows.splice(installationTypeRowIndex + 1, 0, {
        info: { id: sectionId, panelClass: 'full-width' },
        items: [{ id: sectionId, type: RowItemType.Section, section: installationConfig }],
    });

    return basicInfoConfig;
};

function pickInstallationTypeConfig(installationSeries: number) {
    if (installationSeries === InstallationSeries.Channel) {
        return channelInstallationConfig;
    } else if (installationSeries === InstallationSeries.Elevation) {
        return elevationInstallationConfig;
    }
}

function getInstallationTypeSectionId(installationSeries: number) {
    if (installationSeries === InstallationSeries.Channel) {
        return InstallationTypeSectionIds.Channel;
    } else if (installationSeries === InstallationSeries.Elevation) {
        return InstallationTypeSectionIds.Elevation;
    }
}
